import { Fragment, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  CloseButton,
  Code,
  Container,
  Divider,
  Group,
  Loader,
  Modal,
  MultiSelect,
  SegmentedControl,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconBrandTelegram, IconBuildingStore, IconChevronDown, IconChevronRight, IconLink, IconPlus, IconRefresh, IconTrash } from '@tabler/icons-react'
import type {
  AdvertiserDto,
  CreateTgChatBindingRequest,
  ProjectDto,
  TgChatBindingDto,
  TgChatJoinDto,
} from '@ledokol/contracts'
import { AppLayout } from '../features/projects/app-shell'
import { useAuthStore } from '../features/auth/store'
import {
  createTgBinding,
  deleteTgBinding,
  fetchOrphanChats,
  fetchTgBindings,
  updateTgBinding,
} from '../shared/api/tg-bindings'
import {
  createAdvertiser,
  deleteAdvertiser,
  fetchAdvertisers,
  setProjectAdvertiser,
  updateAdvertiser,
  upsertForumChat,
} from '../shared/api/advertisers'
import { fetchProjects } from '../shared/api/projects'
import { fetchDepartments, type DepartmentRow } from '../shared/api/departments'
import { parseChatTitle } from '../shared/parse-chat-title'

/**
 * /admin/tg-bindings — две секции:
 *  1. «Привязанные» — `GET /tg_chat_bindings` (active+inactive);
 *  2. «Бот добавлен, не привязан (orphan)» — `GET /tg_chat_joins?has_binding=false` (W3).
 *
 * RBAC: admin-only (sidebar пункт виден только admin'у; on mount — редирект для остальных).
 *
 * BigInt-safe: `tg_chat_id` — string ВЕЗДЕ (приходит от api строкой, в форму кладётся строкой,
 * уходит в POST/PATCH строкой). Никаких parseInt/Number на -100…
 *
 * Deep-link `/admin/tg-bindings/new?tg_chat_id=…&chat_title=…` — открывает страницу со сразу
 * открытой модалкой «Привязать чат» с предзаполненными полями (для admin-ветки welcome из W4).
 */
export function AdminTgBindingsPage() {
  const { user } = useAuthStore()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const { mode } = useParams<{ mode?: string }>()

  // Защита на клиенте (server даст 403 даже без неё — но UX); admin pure.
  useEffect(() => {
    if (user && user.role !== 'admin') navigate('/projects', { replace: true })
  }, [user, navigate])

  const [bindings, setBindings] = useState<TgChatBindingDto[]>([])
  const [orphans, setOrphans] = useState<TgChatJoinDto[]>([])
  const [projects, setProjects] = useState<ProjectDto[]>([])
  const [departments, setDepartments] = useState<DepartmentRow[]>([])
  const [advertisers, setAdvertisers] = useState<AdvertiserDto[]>([])
  const [loading, setLoading] = useState(true)

  // Состояние модалок
  const [createOpen, setCreateOpen] = useState(false)
  const [createPrefill, setCreatePrefill] = useState<{ tg_chat_id: string; chat_title: string | null } | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<TgChatBindingDto | null>(null)

  async function reload() {
    setLoading(true)
    try {
      const [b, o, p, d, a] = await Promise.all([
        fetchTgBindings(),
        fetchOrphanChats(),
        fetchProjects(),
        fetchDepartments(),
        fetchAdvertisers(),
      ])
      setBindings(b)
      setOrphans(o)
      setProjects(p)
      setDepartments(d)
      setAdvertisers(a)
    } catch (e) {
      notifications.show({ color: 'red', title: 'Ошибка загрузки', message: String(e) })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void reload()
  }, [])

  // Deep-link: /admin/tg-bindings/new?tg_chat_id=…&chat_title=… → открываем модалку с предзаполнением.
  useEffect(() => {
    if (mode === 'new') {
      const tg_chat_id = params.get('tg_chat_id') ?? ''
      const chat_title = params.get('chat_title') // может быть null
      if (tg_chat_id) {
        setCreatePrefill({ tg_chat_id, chat_title })
        setCreateOpen(true)
      }
    }
  }, [mode, params])

  if (!user || user.role !== 'admin') return null

  return (
    <AppLayout>
      <Container size="xl" py="md">
        <Group justify="space-between" mb="md">
          <Group gap="xs">
            <IconBrandTelegram size={28} stroke={1.6} color="var(--mantine-color-blue-6)" />
            <Title order={2}>Чаты Telegram</Title>
          </Group>
          <Group gap="xs">
            <Button leftSection={<IconRefresh size={16} />} variant="default" onClick={reload} loading={loading}>
              Обновить
            </Button>
            <Button leftSection={<IconPlus size={16} />} onClick={() => { setCreatePrefill(null); setCreateOpen(true) }}>
              Привязать чат
            </Button>
          </Group>
        </Group>

        {loading && bindings.length === 0 && <Loader />}

        {/* Секция 1 — привязанные */}
        <BoundSection
          rows={bindings}
          projects={projects}
          onChanged={reload}
          onDelete={(b) => setConfirmDelete(b)}
        />

        <Divider my="xl" />

        {/* Секция 2 — orphan (есть welcome, нет binding) */}
        <OrphanSection
          rows={orphans}
          onBind={(r) => {
            setCreatePrefill({ tg_chat_id: r.tg_chat_id, chat_title: r.chat_title })
            setCreateOpen(true)
          }}
        />

        <Divider my="xl" />

        {/* Секция 3 — крупные рекламодатели (топики/несколько групп) */}
        <AdvertisersSection advertisers={advertisers} projects={projects} onChanged={reload} />

        <Divider my="xl" />

        {/* Секция 4 — конфиг форум-чата (авто-создание под-проектов по топикам) */}
        <ForumChatSection advertisers={advertisers} departments={departments} onChanged={reload} />

        {/* Модалка «Привязать чат» */}
        <CreateBindingModal
          opened={createOpen}
          onClose={() => { setCreateOpen(false); setCreatePrefill(null) }}
          prefill={createPrefill}
          projects={projects}
          departments={departments}
          onCreated={() => { reload(); setCreateOpen(false); setCreatePrefill(null) }}
        />

        {/* Confirm-модалка на удаление */}
        <ConfirmDeleteModal
          binding={confirmDelete}
          onClose={() => setConfirmDelete(null)}
          onConfirmed={() => { reload(); setConfirmDelete(null) }}
        />
      </Container>
    </AppLayout>
  )
}

// ── Секция 1: привязанные ────────────────────────────────────────────────────

interface ChatGroup {
  tgChatId: string
  title: string | null
  bindings: TgChatBindingDto[]
}

/** Группировка привязок по чату: whole-chat первым, затем топики по возрастанию. */
function groupBindingsByChat(rows: TgChatBindingDto[]): ChatGroup[] {
  const map = new Map<string, ChatGroup>()
  for (const b of rows) {
    const g = map.get(b.tg_chat_id) ?? { tgChatId: b.tg_chat_id, title: null, bindings: [] }
    g.bindings.push(b)
    if (!g.title && b.chat_title) g.title = b.chat_title
    map.set(b.tg_chat_id, g)
  }
  const order = (b: TgChatBindingDto) => (b.message_thread_id ? Number(b.message_thread_id) : -1)
  for (const g of map.values()) g.bindings.sort((a, b) => order(a) - order(b))
  return [...map.values()]
}

function BoundSection({
  rows,
  projects,
  onChanged,
  onDelete,
}: {
  rows: TgChatBindingDto[]
  projects: ProjectDto[]
  onChanged: () => void
  onDelete: (b: TgChatBindingDto) => void
}) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const groups = useMemo(() => groupBindingsByChat(rows), [rows])

  function toggle(chatId: string) {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(chatId)) next.delete(chatId)
      else next.add(chatId)
      return next
    })
  }

  return (
    <Stack gap="xs">
      <Title order={4}>Привязанные чаты ({groups.length})</Title>
      {groups.length === 0 ? (
        <Text c="dimmed" size="sm">
          Пока нет привязок. Бот добавлен в чат? Посмотрите секцию «orphan» ниже.
        </Text>
      ) : (
        <Table striped withTableBorder verticalSpacing="xs">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Чат</Table.Th>
              <Table.Th>tg_chat_id</Table.Th>
              <Table.Th>Топик</Table.Th>
              <Table.Th>Проект</Table.Th>
              <Table.Th>Статус</Table.Th>
              <Table.Th>Действия</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {groups.map((g) => {
              // Шапка = «общий» (whole-chat) binding, иначе первый. Дети = остальные (топики).
              const header = g.bindings.find((b) => !b.message_thread_id) ?? g.bindings[0]!
              const children = g.bindings.filter((b) => b !== header)
              const isOpen = expanded.has(g.tgChatId)
              return (
                <Fragment key={g.tgChatId}>
                  <Table.Tr>
                    <Table.Td>
                      <Group gap={6} wrap="nowrap">
                        {children.length > 0 ? (
                          <span
                            onClick={() => toggle(g.tgChatId)}
                            style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}
                          >
                            {isOpen ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}
                          </span>
                        ) : (
                          <span style={{ display: 'inline-block', width: 16 }} />
                        )}
                        {g.title ?? <Text c="dimmed" size="sm">—</Text>}
                      </Group>
                    </Table.Td>
                    <Table.Td><Code>{g.tgChatId}</Code></Table.Td>
                    <BindingDetailCells b={header} projects={projects} onChanged={onChanged} onDelete={onDelete} />
                  </Table.Tr>
                  {isOpen &&
                    children.map((b) => (
                      <Table.Tr key={b.id}>
                        <Table.Td>
                          <span style={{ paddingLeft: 28 }}>
                            {b.topic_name ?? <Text c="dimmed" size="sm" component="span">Тема {b.message_thread_id}</Text>}
                          </span>
                        </Table.Td>
                        <Table.Td />
                        <BindingDetailCells b={b} projects={projects} onChanged={onChanged} onDelete={onDelete} />
                      </Table.Tr>
                    ))}
                </Fragment>
              )
            })}
          </Table.Tbody>
        </Table>
      )}
    </Stack>
  )
}

/** Хвостовые 4 ячейки строки привязки: Топик · Проект · Статус · Действия. */
function BindingDetailCells({
  b,
  projects,
  onChanged,
  onDelete,
}: {
  b: TgChatBindingDto
  projects: ProjectDto[]
  onChanged: () => void
  onDelete: (b: TgChatBindingDto) => void
}) {
  return (
    <>
      <Table.Td>
        {b.message_thread_id ? (
          <Badge variant="light" color="grape" size="sm">топик {b.message_thread_id}</Badge>
        ) : (
          <Badge variant="light" color="gray" size="sm">общий</Badge>
        )}
      </Table.Td>
      <Table.Td>
        {b.project ? (
          <Badge variant="light" color="blue">{b.project.name}</Badge>
        ) : (
          <Badge variant="light" color="orange">не привязан</Badge>
        )}
      </Table.Td>
      <Table.Td>
        <Badge variant="light" color={b.is_active ? 'green' : 'gray'}>
          {b.is_active ? 'active' : 'inactive'}
        </Badge>
      </Table.Td>
      <Table.Td>
        <Group gap="xs">
          <RebindControl binding={b} projects={projects} onDone={onChanged} />
          <Button
            variant="subtle"
            color={b.is_active ? 'gray' : 'green'}
            size="xs"
            onClick={async () => {
              try {
                await updateTgBinding(b.id, { is_active: !b.is_active })
                notifications.show({ color: 'green', message: b.is_active ? 'Деактивирована' : 'Активирована' })
                onChanged()
              } catch (e) {
                notifications.show({ color: 'red', message: String(e) })
              }
            }}
          >
            {b.is_active ? 'Деактивировать' : 'Активировать'}
          </Button>
          <Button variant="subtle" color="red" size="xs" leftSection={<IconTrash size={14} />} onClick={() => onDelete(b)}>
            Удалить
          </Button>
        </Group>
      </Table.Td>
    </>
  )
}

function RebindControl({
  binding,
  projects,
  onDone,
}: {
  binding: TgChatBindingDto
  projects: ProjectDto[]
  onDone: () => void
}) {
  const [opened, setOpened] = useState(false)
  const [target, setTarget] = useState<string | null>(binding.project?.id ?? null)
  const projectOptions = useMemo(
    () => projects.map((p) => ({ value: p.id, label: p.name })),
    [projects],
  )
  return (
    <>
      <Button variant="subtle" size="xs" leftSection={<IconLink size={14} />} onClick={() => setOpened(true)}>
        Перепривязать
      </Button>
      <Modal opened={opened} onClose={() => setOpened(false)} title={`Перепривязать чат «${binding.chat_title ?? binding.tg_chat_id}»`} centered>
        <Stack>
          <Select
            label="Проект"
            placeholder="Выберите проект"
            data={projectOptions}
            searchable
            value={target}
            onChange={setTarget}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setOpened(false)}>
              Отмена
            </Button>
            <Button
              disabled={!target}
              onClick={async () => {
                try {
                  await updateTgBinding(binding.id, { project_id: target })
                  notifications.show({ color: 'green', message: 'Перепривязан' })
                  setOpened(false)
                  onDone()
                } catch (e) {
                  notifications.show({ color: 'red', message: String(e) })
                }
              }}
            >
              Применить
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  )
}

// ── Секция 2: orphan-чаты ────────────────────────────────────────────────────

function OrphanSection({
  rows,
  onBind,
}: {
  rows: TgChatJoinDto[]
  onBind: (r: TgChatJoinDto) => void
}) {
  return (
    <Stack gap="xs">
      <Group gap="xs">
        <Title order={4}>Бот добавлен, не привязан ({rows.length})</Title>
        <Badge variant="light" color="orange">orphan</Badge>
      </Group>
      <Text c="dimmed" size="sm">
        Бот появился в этих чатах (welcome отправлен), но привязка к проекту ещё не настроена.
        Пока чат не привязан, бот <b>не реагирует</b> на сообщения-поручения.
      </Text>
      {rows.length === 0 ? (
        <Alert color="gray" variant="light">
          Нет orphan-чатов. Бот добавлен только в привязанные.
        </Alert>
      ) : (
        <Table striped withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Чат</Table.Th>
              <Table.Th>tg_chat_id</Table.Th>
              <Table.Th>Кто добавил</Table.Th>
              <Table.Th>Когда</Table.Th>
              <Table.Th>Действия</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map((r) => (
              <Table.Tr key={r.tg_chat_id}>
                <Table.Td>{r.chat_title ?? <Text c="dimmed" size="sm">—</Text>}</Table.Td>
                <Table.Td><Code>{r.tg_chat_id}</Code></Table.Td>
                <Table.Td>
                  {r.added_by_full_name ?? <Text c="dimmed" size="sm">—</Text>}
                  {r.added_by_role && (
                    <Text size="xs" c="dimmed">
                      ({r.added_by_role})
                    </Text>
                  )}
                </Table.Td>
                <Table.Td>{new Date(r.joined_at).toLocaleString('ru-RU')}</Table.Td>
                <Table.Td>
                  <Button size="xs" leftSection={<IconLink size={14} />} onClick={() => onBind(r)}>
                    Привязать
                  </Button>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </Stack>
  )
}

// ── Модалка «Привязать чат» (создать новый binding) ─────────────────────────

function CreateBindingModal({
  opened,
  onClose,
  prefill,
  projects,
  departments,
  onCreated,
}: {
  opened: boolean
  onClose: () => void
  prefill: { tg_chat_id: string; chat_title: string | null } | null
  projects: ProjectDto[]
  departments: DepartmentRow[]
  onCreated: () => void
}) {
  // Два режима: existing (project_id) / new (project_name + departments).
  const [mode, setMode] = useState<'existing' | 'new'>('new')
  const [tgChatId, setTgChatId] = useState('')
  const [chatTitle, setChatTitle] = useState('')
  const [projectId, setProjectId] = useState<string | null>(null)
  const [projectName, setProjectName] = useState('')
  const [departmentIds, setDepartmentIds] = useState<string[]>([])
  const [submitting, setSubmitting] = useState(false)

  // Сброс/предзаполнение при открытии
  useEffect(() => {
    if (opened) {
      const tg = prefill?.tg_chat_id ?? ''
      const title = prefill?.chat_title ?? null
      setTgChatId(tg) // string как есть, никаких parseInt
      setChatTitle(title ?? '')
      const parsed = parseChatTitle(title) // эвристика заполняет input, admin может править
      setProjectName(parsed ?? '')
      setProjectId(null)
      setDepartmentIds([])
      setMode('new')
    }
  }, [opened, prefill])

  const projectOptions = useMemo(
    () => projects.map((p) => ({ value: p.id, label: p.name })),
    [projects],
  )
  const departmentOptions = useMemo(
    () => departments.map((d) => ({ value: d.id, label: d.name })),
    [departments],
  )

  async function submit() {
    if (!tgChatId.trim()) {
      notifications.show({ color: 'red', message: 'Укажите tg_chat_id' })
      return
    }
    const body: CreateTgChatBindingRequest =
      mode === 'existing'
        ? { tg_chat_id: tgChatId.trim(), chat_title: chatTitle || null, project_id: projectId ?? undefined }
        : { tg_chat_id: tgChatId.trim(), chat_title: chatTitle || null, project_name: projectName.trim(), departments: departmentIds }

    setSubmitting(true)
    try {
      await createTgBinding(body)
      notifications.show({ color: 'green', message: 'Чат привязан к проекту' })
      onCreated()
    } catch (e) {
      const msg = (e as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error?.message ?? String(e)
      notifications.show({ color: 'red', title: 'Не удалось привязать', message: msg })
    } finally {
      setSubmitting(false)
    }
  }

  const canSubmit =
    tgChatId.trim().length > 0 &&
    (mode === 'existing'
      ? !!projectId
      : projectName.trim().length > 0 && departmentIds.length > 0)

  return (
    <Modal opened={opened} onClose={onClose} title="Привязать чат к проекту" centered size="lg">
      <Stack>
        <TextInput
          label="tg_chat_id"
          description="Из лога бота или orphan-секции. Супергруппа = -100…"
          value={tgChatId}
          onChange={(e) => setTgChatId(e.currentTarget.value)}
          required
          placeholder="-1001234567890"
        />
        <TextInput
          label="Название чата (для отображения)"
          value={chatTitle}
          onChange={(e) => setChatTitle(e.currentTarget.value)}
          placeholder="Disney | Workgroup"
        />

        <SegmentedControl
          fullWidth
          value={mode}
          onChange={(v) => setMode(v as 'existing' | 'new')}
          data={[
            { value: 'new', label: 'Создать новый проект' },
            { value: 'existing', label: 'Привязать к существующему' },
          ]}
        />

        {mode === 'new' ? (
          <>
            <TextInput
              label="Название проекта"
              description="Предзаполнено эвристикой из «названия чата». Проверьте и поправьте при необходимости."
              value={projectName}
              onChange={(e) => setProjectName(e.currentTarget.value)}
              required
            />
            <MultiSelect
              label="Отделы"
              description="Минимум один. От этого зависит, в каких сайдбарах появится проект."
              data={departmentOptions}
              value={departmentIds}
              onChange={setDepartmentIds}
              required
              searchable
            />
            <Alert variant="light" color="blue">
              Будет создан новый проект (lead = вы). Если проект с таким именем уже есть — он будет
              найден (без дублирования), и чат привяжется к нему.
            </Alert>
          </>
        ) : (
          <Select
            label="Проект"
            placeholder="Выберите проект"
            data={projectOptions}
            searchable
            value={projectId}
            onChange={setProjectId}
            required
          />
        )}

        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={onClose} disabled={submitting}>
            Отмена
          </Button>
          <Button onClick={submit} loading={submitting} disabled={!canSubmit}>
            Привязать
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}

// ── Confirm-модалка на удаление ─────────────────────────────────────────────

function ConfirmDeleteModal({
  binding,
  onClose,
  onConfirmed,
}: {
  binding: TgChatBindingDto | null
  onClose: () => void
  onConfirmed: () => void
}) {
  const [submitting, setSubmitting] = useState(false)
  if (!binding) return null
  return (
    <Modal opened={!!binding} onClose={onClose} title="Удалить привязку?" centered>
      <Stack>
        <Text>
          Чат «{binding.chat_title ?? binding.tg_chat_id}» будет отвязан от проекта{' '}
          <b>{binding.project?.name ?? '—'}</b>.
        </Text>
        <Alert color="yellow" variant="light">
          Уже созданные из этого чата задачи <b>останутся</b> в проекте. Если бот всё ещё в чате —
          он перестанет реагировать на сообщения-поручения (orphan).
        </Alert>
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose} disabled={submitting}>
            Отмена
          </Button>
          <Button
            color="red"
            loading={submitting}
            onClick={async () => {
              setSubmitting(true)
              try {
                await deleteTgBinding(binding.id)
                notifications.show({ color: 'green', message: 'Привязка удалена' })
                onConfirmed()
              } catch (e) {
                notifications.show({ color: 'red', message: String(e) })
              } finally {
                setSubmitting(false)
              }
            }}
          >
            Удалить
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}

// ── Секция 3: крупные рекламодатели ──────────────────────────────────────────

function AdvertisersSection({
  advertisers,
  projects,
  onChanged,
}: {
  advertisers: AdvertiserDto[]
  projects: ProjectDto[]
  onChanged: () => void
}) {
  const [name, setName] = useState('')
  const [submitting, setSubmitting] = useState(false)

  async function add() {
    if (!name.trim()) return
    setSubmitting(true)
    try {
      await createAdvertiser({ name: name.trim() })
      notifications.show({ color: 'green', message: 'Рекламодатель создан' })
      setName('')
      onChanged()
    } catch (e) {
      notifications.show({ color: 'red', message: String(e) })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Stack gap="xs">
      <Group gap="xs">
        <IconBuildingStore size={22} color="var(--mantine-color-blue-6)" />
        <Title order={4}>Крупные рекламодатели ({advertisers.length})</Title>
      </Group>
      <Text c="dimmed" size="sm">
        Рекламодатель группирует под-проекты (топики форум-группы или несколько ТГ-групп) в одну
        строку списка. Уже созданные проекты привяжите вручную в колонке «Проекты», топики
        форум-группы создаются автоматически (см. «Форум-чат» ниже).
      </Text>

      <Group align="flex-end" gap="xs">
        <TextInput
          label="Название рекламодателя"
          placeholder="Яндекс"
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
          w={280}
        />
        <Button leftSection={<IconPlus size={16} />} onClick={add} loading={submitting} disabled={!name.trim()}>
          Добавить
        </Button>
      </Group>

      {advertisers.length > 0 && (
        <Table striped withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Название</Table.Th>
              <Table.Th>Проекты</Table.Th>
              <Table.Th>Действия</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {advertisers.map((a) => (
              <AdvertiserRow key={a.id} advertiser={a} projects={projects} onChanged={onChanged} />
            ))}
          </Table.Tbody>
        </Table>
      )}
    </Stack>
  )
}

function AdvertiserRow({
  advertiser,
  projects,
  onChanged,
}: {
  advertiser: AdvertiserDto
  projects: ProjectDto[]
  onChanged: () => void
}) {
  const [editing, setEditing] = useState(false)
  const [name, setName] = useState(advertiser.name)
  const [toAttach, setToAttach] = useState<string | null>(null)

  const assigned = projects.filter((p) => p.advertiser?.id === advertiser.id)
  // Привязывать можно только свободные проекты (без рекламодателя) — иначе «угон» чужого.
  const free = projects.filter((p) => !p.advertiser)

  async function attach(projectId: string) {
    try {
      await setProjectAdvertiser(projectId, { advertiser_id: advertiser.id })
      notifications.show({ color: 'green', message: 'Проект привязан' })
      setToAttach(null)
      onChanged()
    } catch (e) {
      notifications.show({ color: 'red', message: String(e) })
    }
  }
  async function detach(projectId: string) {
    try {
      await setProjectAdvertiser(projectId, { advertiser_id: null })
      notifications.show({ color: 'green', message: 'Проект отвязан' })
      onChanged()
    } catch (e) {
      notifications.show({ color: 'red', message: String(e) })
    }
  }

  return (
    <Table.Tr>
      <Table.Td>
        {editing ? (
          <TextInput value={name} onChange={(e) => setName(e.currentTarget.value)} size="xs" w={220} />
        ) : (
          <Text fw={500}>{advertiser.name}</Text>
        )}
      </Table.Td>
      <Table.Td>
        <Stack gap={6}>
          <Group gap={6}>
            {assigned.length === 0 && <Text c="dimmed" size="sm">нет привязанных проектов</Text>}
            {assigned.map((p) => (
              <Badge
                key={p.id}
                variant="light"
                color="blue"
                rightSection={<CloseButton size="xs" aria-label="Отвязать" onClick={() => detach(p.id)} />}
              >
                {p.name}
              </Badge>
            ))}
          </Group>
          <Group gap={6} wrap="nowrap">
            <Select
              placeholder={free.length ? 'Привязать проект…' : 'Нет свободных проектов'}
              data={free.map((p) => ({ value: p.id, label: p.name }))}
              value={toAttach}
              onChange={setToAttach}
              disabled={free.length === 0}
              searchable
              size="xs"
              w={240}
            />
            <ActionIcon
              variant="light"
              disabled={!toAttach}
              aria-label="Привязать"
              onClick={() => toAttach && attach(toAttach)}
            >
              <IconPlus size={16} />
            </ActionIcon>
          </Group>
        </Stack>
      </Table.Td>
      <Table.Td>
        <Group gap="xs">
          {editing ? (
            <>
              <Button
                size="xs"
                onClick={async () => {
                  try {
                    await updateAdvertiser(advertiser.id, { name: name.trim() })
                    notifications.show({ color: 'green', message: 'Переименован' })
                    setEditing(false)
                    onChanged()
                  } catch (e) {
                    notifications.show({ color: 'red', message: String(e) })
                  }
                }}
              >
                Сохранить
              </Button>
              <Button size="xs" variant="default" onClick={() => { setEditing(false); setName(advertiser.name) }}>
                Отмена
              </Button>
            </>
          ) : (
            <>
              <Button size="xs" variant="subtle" onClick={() => setEditing(true)}>
                Переименовать
              </Button>
              <Button
                size="xs"
                variant="subtle"
                color="red"
                leftSection={<IconTrash size={14} />}
                onClick={async () => {
                  try {
                    await deleteAdvertiser(advertiser.id)
                    notifications.show({ color: 'green', message: 'Удалён (под-проекты стали обычными)' })
                    onChanged()
                  } catch (e) {
                    notifications.show({ color: 'red', message: String(e) })
                  }
                }}
              >
                Удалить
              </Button>
            </>
          )}
        </Group>
      </Table.Td>
    </Table.Tr>
  )
}

// ── Секция 4: конфиг форум-чата ──────────────────────────────────────────────

function ForumChatSection({
  advertisers,
  departments,
  onChanged,
}: {
  advertisers: AdvertiserDto[]
  departments: DepartmentRow[]
  onChanged: () => void
}) {
  const [tgChatId, setTgChatId] = useState('')
  const [advertiserId, setAdvertiserId] = useState<string | null>(null)
  const [autoCreate, setAutoCreate] = useState(true)
  const [departmentIds, setDepartmentIds] = useState<string[]>([])
  const [submitting, setSubmitting] = useState(false)

  const advOptions = useMemo(() => advertisers.map((a) => ({ value: a.id, label: a.name })), [advertisers])
  const deptOptions = useMemo(() => departments.map((d) => ({ value: d.id, label: d.name })), [departments])

  async function save() {
    if (!tgChatId.trim() || !advertiserId) return
    setSubmitting(true)
    try {
      await upsertForumChat({
        tg_chat_id: tgChatId.trim(),
        advertiser_id: advertiserId,
        auto_create_topics: autoCreate,
        default_department_ids: departmentIds,
      })
      notifications.show({ color: 'green', message: 'Форум-чат настроен' })
      setTgChatId('')
      onChanged()
    } catch (e) {
      const msg = (e as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error?.message ?? String(e)
      notifications.show({ color: 'red', title: 'Не удалось сохранить', message: msg })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Stack gap="xs">
      <Title order={4}>Форум-чат (авто-создание под-проектов по топикам)</Title>
      <Text c="dimmed" size="sm">
        Пометьте ТГ-группу с топиками как форум рекламодателя. Каждый топик станет под-проектом
        «{'{Рекламодатель}'} · {'{Топик}'}» автоматически. Обычные группы настраивать здесь не нужно.
      </Text>
      <Group align="flex-end" gap="xs" wrap="wrap">
        <TextInput
          label="tg_chat_id форум-группы"
          placeholder="-1001234567890"
          value={tgChatId}
          onChange={(e) => setTgChatId(e.currentTarget.value)}
          w={220}
        />
        <Select
          label="Рекламодатель"
          placeholder="Выберите"
          data={advOptions}
          value={advertiserId}
          onChange={setAdvertiserId}
          searchable
          w={220}
        />
        <MultiSelect
          label="Отделы под-проектов"
          data={deptOptions}
          value={departmentIds}
          onChange={setDepartmentIds}
          searchable
          w={260}
        />
        <Switch
          label="Авто-создание топиков"
          checked={autoCreate}
          onChange={(e) => setAutoCreate(e.currentTarget.checked)}
          mb={6}
        />
        <Button onClick={save} loading={submitting} disabled={!tgChatId.trim() || !advertiserId}>
          Сохранить
        </Button>
      </Group>
    </Stack>
  )
}

